/**
 * SettingsScene — the calm, opt-in control room. Everything here is a gentle dial,
 * never a nag: the Cozy↔Spicy difficulty slider, the LLM-chat master toggle, a daily
 * token-budget dial with a plain cost readout, a reduced-motion toggle, export/import
 * of the save-to-file (the owner OWNS their world), and a Credits screen that thanks
 * the PMD spriters (the CC-NC attribution the project requires).
 *
 * The renderer owns no rules: every change flows through `session.updateSettings`
 * (which persists), and reduced-motion also flips the shared SceneFlow flag so the
 * whole app's transitions stay in sync immediately.
 *
 * Touch-first + responsive throughout (Layout + the cozy UI kit): one scrolling
 * column of soft rows that reflow to any width, every target a big tap target, and a
 * clean "Back" exit (closing mid-anything is always fine).
 */
import Phaser from 'phaser';
import type { Difficulty, Settings } from '@/shared';
import { getSession } from './session.js';
import { COLORS, CSS, FONT, SPACING, TOUCH_TARGET } from './ui/theme.js';
import { Panel } from './ui/Panel.js';
import { Button } from './ui/Button.js';
import { ScrollList } from './ui/ScrollList.js';
import { FocusRing } from './ui/FocusRing.js';
import { moveGrid } from './ui/gridNav.js';
import { domTextFieldFocused } from './ui/keyboardGate.js';
import { Toast } from './ui/Toast.js';
import { Layout } from './layout/index.js';
import { fadeIn, goTo, isReducedMotion, setReducedMotion } from './flow.js';
import type { GameSession } from '@/session';

/** The difficulty stops, in cozy→spicy order, with a plain warm description. */
const DIFFICULTY_STOPS: readonly { id: Difficulty; label: string; blurb: string }[] = [
  { id: 'cozy', label: 'Cozy', blurb: 'Gentle foes. The world bends to keep you comfortable.' },
  { id: 'mild', label: 'Mild', blurb: 'A little more bite, still kind. A pleasant middle.' },
  {
    id: 'spicy',
    label: 'Spicy',
    blurb: 'A real test when you want one. Never unfair, never punishing.',
  },
];

/** Daily token-budget stops (client mirror; the server enforces the true cap). */
const BUDGET_STOPS: readonly number[] = [10_000, 25_000, 50_000, 100_000, 200_000];

/** Rough Haiku-class blended price ($ per 1M tokens) for the cozy cost readout. */
const APPROX_USD_PER_MTOK = 3;

const CONTENT_MAX_WIDTH = 560;
const ROW_PAD = SPACING.lg;

/** The focusable setting cards, by keyboard-nav kind. */
type RowKind = 'difficulty' | 'llm' | 'budget' | 'motion' | 'save' | 'credits';

export class SettingsScene extends Phaser.Scene {
  private session!: GameSession;
  private layout!: Layout;
  private reduced = false;

  private heading?: Phaser.GameObjects.Text;
  private backButton?: Button;
  private list?: ScrollList;

  /** Live local mirror of settings; committed to the session on each change. */
  private settings!: Settings;

  /** The content width the rows were last MEASURED at; rebuild when it changes. */
  private builtWidth = -1;

  // Rows that need re-rendering when a value changes.
  private difficultyButtons: { id: Difficulty; button: Button }[] = [];
  private difficultyBlurb?: Phaser.GameObjects.Text;
  private llmButton?: Button;
  private llmHint?: Phaser.GameObjects.Text;
  private budgetButtons: { value: number; button: Button }[] = [];
  private budgetReadout?: Phaser.GameObjects.Text;
  private motionButton?: Button;

  // ── keyboard nav ────────────────────────────────────────────────────────────
  /** Each focusable setting card, in visual order (for the up/down cursor + ring). */
  private rowFocus: { cy: number; height: number; kind: RowKind }[] = [];
  private rowIndex = -1;
  private listViewH = 0;
  private focusRing?: FocusRing;
  private keyHandler?: (e: KeyboardEvent) => void;
  /** The open credits overlay, so Esc closes it (else falls through to Back). */
  private creditsOverlay?: Phaser.GameObjects.Container;

  constructor() {
    super('Settings');
  }

  create(): void {
    // Phaser REUSES this scene instance, so create() re-runs on every RE-ENTRY (Town →
    // Settings → Town → Settings). The difficulty/budget BUTTON arrays survive between
    // visits, so buildRows() would PUSH a second set onto the stale one (the refresh
    // loops would then touch destroyed buttons). resetState() clears them first.
    this.resetState();

    this.cameras.main.setBackgroundColor(COLORS.bg);
    this.session = getSession(this);
    this.settings = { ...this.session.currentSettings };
    this.layout = Layout.attach(this);
    this.reduced = isReducedMotion();

    this.heading = this.add
      .text(0, 0, 'Settings', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.xl}px`,
        color: CSS.text,
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5);

    this.backButton = new Button(this, 0, 0, {
      label: '‹ Back',
      variant: 'ghost',
      width: 120,
      reducedMotion: this.reduced,
      onClick: () => this.goBack(),
    });

    this.list = new ScrollList(this, 0, 0, { width: 100, height: 100 });
    this.buildRows();
    this.focusRing = new FocusRing(this, { depth: 50 });

    this.relayout();
    this.layout.on('change', () => this.relayout());
    this.setupKeyboard();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
    fadeIn(this);
  }

  /**
   * Full keyboard nav: up/down move between setting cards, left/right adjust the focused
   * dial (difficulty / budget) or flip a toggle, Enter activates (toggle / open), Esc
   * closes the credits overlay else returns to Town. Ignored while a DOM text field is
   * focused. Cleaned up on SHUTDOWN.
   */
  private setupKeyboard(): void {
    this.keyHandler = (e: KeyboardEvent): void => {
      if (typeof document !== 'undefined' && domTextFieldFocused(document.activeElement)) return;
      // With the credits overlay open, Esc closes it; other keys are swallowed underneath.
      if (this.creditsOverlay) {
        if (e.key === 'Escape') {
          e.preventDefault();
          this.closeCredits();
        }
        return;
      }
      const n = this.rowFocus.length;
      switch (e.key) {
        case 'ArrowUp':
        case 'w':
          e.preventDefault();
          this.setRow(this.rowIndex < 0 ? 0 : moveGrid(this.rowIndex, 'up', n, 1));
          break;
        case 'ArrowDown':
        case 's':
          e.preventDefault();
          this.setRow(this.rowIndex < 0 ? 0 : moveGrid(this.rowIndex, 'down', n, 1));
          break;
        case 'ArrowLeft':
        case 'a':
          e.preventDefault();
          this.adjustRow(-1);
          break;
        case 'ArrowRight':
        case 'd':
          e.preventDefault();
          this.adjustRow(1);
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          this.activateRow();
          break;
        case 'Escape':
          e.preventDefault();
          this.goBack();
          break;
        default:
          break;
      }
    };
    if (typeof window !== 'undefined') window.addEventListener('keydown', this.keyHandler);
  }

  /** Seat the keyboard cursor on a setting card: scroll into view + draw the focus ring. */
  private setRow(index: number): void {
    const n = this.rowFocus.length;
    if (n === 0 || !this.list) return;
    this.rowIndex = Math.max(0, Math.min(n - 1, index));
    const row = this.rowFocus[this.rowIndex];
    if (!row) return;
    // Keep the focused card inside the scrolling viewport before drawing the ring.
    const top = row.cy - row.height / 2;
    const bottom = row.cy + row.height / 2;
    const off = this.list.scrollOffset;
    const pad = SPACING.md;
    if (top < off) this.list.scrollTo(top - pad);
    else if (bottom > off + this.listViewH) this.list.scrollTo(bottom - this.listViewH + pad);
    const worldX = this.list.x;
    const worldY = this.list.y + row.cy - this.listViewH / 2 - this.list.scrollOffset;
    this.focusRing?.show(worldX, worldY, this.contentWidth(), row.height);
    this.focusRing?.bringToTop();
  }

  /** Left/right on the focused card: adjust a dial, or flip a toggle. */
  private adjustRow(dir: 1 | -1): void {
    if (this.rowIndex < 0) {
      this.setRow(0);
      return;
    }
    const row = this.rowFocus[this.rowIndex];
    if (!row) return;
    const gdir = dir > 0 ? 'right' : 'left';
    if (row.kind === 'difficulty') {
      const cur = Math.max(
        0,
        DIFFICULTY_STOPS.findIndex((s) => s.id === this.settings.difficulty),
      );
      const next = moveGrid(cur, gdir, DIFFICULTY_STOPS.length, DIFFICULTY_STOPS.length);
      if (next !== cur) void this.setDifficulty(DIFFICULTY_STOPS[next]!.id);
    } else if (row.kind === 'budget') {
      const cur = Math.max(
        0,
        BUDGET_STOPS.findIndex((v) => v === this.nearestBudgetStop()),
      );
      const next = moveGrid(cur, gdir, BUDGET_STOPS.length, BUDGET_STOPS.length);
      if (next !== cur) void this.setBudget(BUDGET_STOPS[next]!);
    } else if (row.kind === 'llm') {
      const want = dir > 0;
      if (this.settings.llmEnabled !== want) void this.toggleLlm();
    } else if (row.kind === 'motion') {
      const want = dir > 0;
      if (this.settings.reducedMotion !== want) void this.toggleMotion();
    }
  }

  /** Enter on the focused card: toggle a switch, or open (credits / export). */
  private activateRow(): void {
    if (this.rowIndex < 0) {
      this.setRow(0);
      return;
    }
    const row = this.rowFocus[this.rowIndex];
    if (!row) return;
    switch (row.kind) {
      case 'llm':
        void this.toggleLlm();
        break;
      case 'motion':
        void this.toggleMotion();
        break;
      case 'save':
        this.exportSave();
        break;
      case 'credits':
        this.openCredits();
        break;
      default:
        break; // difficulty / budget adjust via left/right, Enter is a no-op
    }
  }

  /** The budget stop nearest the live value (matches refreshBudget's highlight logic). */
  private nearestBudgetStop(): number {
    const current = this.settings.dailyTokenBudget;
    return BUDGET_STOPS.reduce((best, v) =>
      Math.abs(v - current) < Math.abs(best - current) ? v : best,
    );
  }

  private closeCredits(): void {
    this.creditsOverlay?.destroy();
    this.creditsOverlay = undefined;
  }

  /**
   * Drop every reference a prior visit's create() populated so re-entry starts clean.
   * Display objects were torn down by the previous SHUTDOWN (teardown()); this clears the
   * arrays/handles so nothing stacks. Safe on a first entry (everything already empty).
   */
  private resetState(): void {
    this.difficultyButtons = [];
    this.budgetButtons = [];
    this.heading = undefined;
    this.backButton = undefined;
    this.list = undefined;
    this.difficultyBlurb = undefined;
    this.llmButton = undefined;
    this.llmHint = undefined;
    this.budgetReadout = undefined;
    this.motionButton = undefined;
    this.rowFocus = [];
    this.rowIndex = -1;
    this.focusRing = undefined;
    this.keyHandler = undefined;
    this.creditsOverlay = undefined;
  }

  /**
   * SHUTDOWN handler — Phaser reuses this scene instance. The ScrollList self-destroys
   * (cascading to the row cards + their buttons), but we still destroy the top-level
   * handles + drop the button-array references so a re-entry never touches a destroyed
   * button while refreshing selection state.
   */
  private teardown(): void {
    this.tweens.killAll();
    this.time.removeAllEvents();
    if (this.keyHandler && typeof window !== 'undefined') {
      window.removeEventListener('keydown', this.keyHandler);
    }
    this.keyHandler = undefined;
    this.focusRing?.destroy();
    this.focusRing = undefined;
    this.creditsOverlay?.destroy();
    this.creditsOverlay = undefined;
    this.rowFocus = [];
    this.list?.destroy();
    this.heading?.destroy();
    this.backButton?.destroy();
    this.difficultyButtons = [];
    this.budgetButtons = [];
    this.heading = undefined;
    this.backButton = undefined;
    this.list = undefined;
    this.difficultyBlurb = undefined;
    this.llmButton = undefined;
    this.llmHint = undefined;
    this.budgetReadout = undefined;
    this.motionButton = undefined;
  }

  // ── rows ──────────────────────────────────────────────────────────────────────

  /** Build the scrolling stack of soft setting cards. */
  private buildRows(): void {
    const w = this.contentWidth();
    this.rowFocus = [];
    let y = 0;
    y = this.addDifficultyRow(w, y);
    y = this.addLlmRow(w, y);
    y = this.addBudgetRow(w, y);
    y = this.addMotionRow(w, y);
    y = this.addSaveRow(w, y);
    y = this.addCreditsRow(w, y);
    y = this.addControlsRow(w, y);
    this.list?.setContentHeight(y);
    this.builtWidth = w;
  }

  /** Register a focusable card so the keyboard cursor + focus ring can find it. */
  private registerRow(topY: number, height: number, kind: RowKind): void {
    this.rowFocus.push({ cy: topY + height / 2, height, kind });
  }

  /**
   * A read-only "Controls" note listing the keyboard/touch keys (walk, confirm, back, menu,
   * cast). Non-focusable — a calm reference, not a control — so keyboard nav skips it.
   */
  private addControlsRow(w: number, y: number): number {
    const sub =
      'Walk: arrows / WASD (or the on-screen pad)  ·  Confirm: Enter  ·  Back: Esc  ·  ' +
      'Menu: M  ·  Cast a move: C';
    const titleBlockH = this.measureTitleHeight(w, 'Controls', sub);
    const height = ROW_PAD + titleBlockH + ROW_PAD;
    const { card } = this.makeCard(w, y, height);
    const top = -height / 2 + ROW_PAD;
    this.addTitle(card, w, top, 'Controls', sub);
    return y + height + SPACING.md;
  }

  /**
   * Tear down and rebuild every card at the CURRENT content width. Called on an
   * orientation/size change so each card RE-MEASURES its wrapped text — a paragraph
   * that wrapped to 4 lines in portrait may be 2 in landscape (and vice-versa), so
   * the card heights must recompute or text would overlap the controls/borders.
   */
  private rebuildRows(): void {
    // Drop the stale per-row handles + button arrays, then clear the list content.
    this.difficultyButtons = [];
    this.budgetButtons = [];
    this.difficultyBlurb = undefined;
    this.llmButton = undefined;
    this.llmHint = undefined;
    this.budgetReadout = undefined;
    this.motionButton = undefined;
    const content = this.list?.content;
    content?.removeAll(true);
    if (this.list) this.list.setContentHeight(0);
    this.buildRows();
  }

  /** A soft card panel placed in the list at vertical cursor `y`; returns next y. */
  private makeCard(
    w: number,
    y: number,
    height: number,
  ): { card: Phaser.GameObjects.Container; cy: number } {
    const cy = y + height / 2;
    const card = this.add.container(0, cy);
    const panel = new Panel(this, 0, 0, { width: w, height, fill: COLORS.surface });
    card.add(panel);
    this.list?.addItem(card as Phaser.GameObjects.Container & { y: number }, height + SPACING.md);
    return { card, cy };
  }

  private addTitle(
    card: Phaser.GameObjects.Container,
    w: number,
    topY: number,
    text: string,
    sub?: string,
    subWrapWidth?: number,
  ): number {
    const wrapW = subWrapWidth ?? w - ROW_PAD * 2;
    const title = this.add.text(-w / 2 + ROW_PAD, topY, text, {
      fontFamily: FONT.family,
      fontSize: `${FONT.size.lg}px`,
      color: CSS.text,
      fontStyle: 'bold',
      wordWrap: { width: wrapW },
    });
    card.add(title);
    let next = topY + title.height + SPACING.xs;
    if (sub) {
      const subText = this.add.text(-w / 2 + ROW_PAD, next, sub, {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.sm}px`,
        color: CSS.textMuted,
        wordWrap: { width: wrapW },
      });
      card.add(subText);
      next += subText.height + SPACING.xs;
    }
    return next;
  }

  // ── difficulty dial ─────────────────────────────────────────────────────────

  private addDifficultyRow(w: number, y: number): number {
    const sub =
      'Slide from cozy to spicy. The world also rubber-bands gently so it is never unfair.';

    // Measure-to-content: the title+subtitle block can wrap to several lines on a
    // ~390px phone. Compute the card height from the MEASURED text plus the controls
    // below it, so help text never overlaps the buttons / the card border / the next
    // card (the root cause of the mobile overlap clusterfix).
    const titleBlockH = this.measureTitleHeight(w, 'Difficulty', sub);

    const segW = Math.min(150, (w - ROW_PAD * 2 - SPACING.sm * 2) / 3);
    const btnH = Math.max(TOUCH_TARGET, FONT.size.md + SPACING.lg);
    // The warm blurb text is set later; measure the LONGEST stop blurb so the card
    // reserves room for the tallest wrap.
    const blurbH = this.measureWrappedHeight(
      DIFFICULTY_STOPS.map((s) => s.blurb).reduce((a, b) => (a.length >= b.length ? a : b)),
      FONT.size.sm,
      w - ROW_PAD * 2,
    );

    const height = ROW_PAD + titleBlockH + SPACING.md + btnH + SPACING.sm + blurbH + ROW_PAD;
    const { card } = this.makeCard(w, y, height);
    this.registerRow(y, height, 'difficulty');
    const top = -height / 2 + ROW_PAD;
    const afterTitle = this.addTitle(card, w, top, 'Difficulty', sub);

    const btnY = afterTitle + SPACING.md + btnH / 2;
    const totalW = segW * 3 + SPACING.sm * 2;
    const startX = -totalW / 2 + segW / 2;
    this.difficultyButtons = [];
    DIFFICULTY_STOPS.forEach((stop, i) => {
      const button = new Button(this, startX + i * (segW + SPACING.sm), btnY, {
        label: stop.label,
        variant: 'ghost',
        width: segW,
        reducedMotion: this.reduced,
        onClick: () => void this.setDifficulty(stop.id),
      });
      card.add(button);
      this.difficultyButtons.push({ id: stop.id, button });
    });

    this.difficultyBlurb = this.add
      .text(0, btnY + btnH / 2 + SPACING.sm, '', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.sm}px`,
        color: CSS.warm,
        align: 'center',
        wordWrap: { width: w - ROW_PAD * 2 },
      })
      .setOrigin(0.5, 0);
    card.add(this.difficultyBlurb);
    this.refreshDifficulty();
    return y + height + SPACING.md;
  }

  private async setDifficulty(id: Difficulty): Promise<void> {
    this.settings = { ...this.settings, difficulty: id };
    this.refreshDifficulty();
    await this.session.updateSettings({ difficulty: id });
  }

  private refreshDifficulty(): void {
    for (const { id, button } of this.difficultyButtons) {
      const on = id === this.settings.difficulty;
      button.setAlpha(on ? 1 : 0.55);
    }
    const stop = DIFFICULTY_STOPS.find((s) => s.id === this.settings.difficulty);
    this.difficultyBlurb?.setText(stop?.blurb ?? '');
  }

  // ── LLM chat toggle ───────────────────────────────────────────────────────────

  private addLlmRow(w: number, y: number): number {
    const sub =
      'When on, the town speaks with fresh, AI-voiced banter. Off, a warm hand-written floor keeps it cozy.';

    // A toggle sits at the top-right; the title block wraps to the LEFT of it, so it
    // must wrap to the width that CLEARS the button (else on a phone the title runs
    // under the toggle). Then the longest hint line is measured below.
    const toggleW = Math.min(110, Math.max(TOUCH_TARGET * 1.8, w * 0.28));
    const titleW = w - ROW_PAD * 2 - toggleW - SPACING.md;
    const titleBlockH = this.measureTitleHeight(w, 'Lively chat', sub, titleW);
    const btnH = Math.max(TOUCH_TARGET, FONT.size.md + SPACING.lg);
    const hintH = this.measureWrappedHeight(
      'Takes effect next time the town wakes (a fresh session).',
      FONT.size.xs,
      w - ROW_PAD * 2,
    );

    const topBlockH = Math.max(titleBlockH, btnH);
    const height = ROW_PAD + topBlockH + SPACING.md + hintH + ROW_PAD;
    const { card } = this.makeCard(w, y, height);
    this.registerRow(y, height, 'llm');
    const top = -height / 2 + ROW_PAD;
    this.addTitle(card, w, top, 'Lively chat', sub, titleW);

    this.llmButton = new Button(this, w / 2 - ROW_PAD - toggleW / 2, top + btnH / 2, {
      label: '',
      variant: 'primary',
      width: toggleW,
      reducedMotion: this.reduced,
      onClick: () => void this.toggleLlm(),
    });
    card.add(this.llmButton);

    this.llmHint = this.add
      .text(-w / 2 + ROW_PAD, height / 2 - ROW_PAD, '', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.xs}px`,
        color: CSS.textFaint,
        wordWrap: { width: w - ROW_PAD * 2 },
      })
      .setOrigin(0, 1);
    card.add(this.llmHint);

    this.refreshLlm();
    return y + height + SPACING.md;
  }

  private async toggleLlm(): Promise<void> {
    const next = !this.settings.llmEnabled;
    this.settings = { ...this.settings, llmEnabled: next };
    this.refreshLlm();
    await this.session.updateSettings({ llmEnabled: next });
  }

  private refreshLlm(): void {
    const on = this.settings.llmEnabled;
    this.llmButton?.setLabel(on ? 'On' : 'Off');
    this.llmButton?.setAlpha(on ? 1 : 0.7);
    this.llmHint?.setText(
      on
        ? 'Takes effect next time the town wakes (a fresh session).'
        : 'The world stays populated and pleasant on the template floor.',
    );
  }

  // ── token budget dial ─────────────────────────────────────────────────────────

  private addBudgetRow(w: number, y: number): number {
    const sub =
      'A soft daily ceiling on AI-voiced lines. Reaching it just quietly hands the town back to the template floor — never a wall.';
    const titleBlockH = this.measureTitleHeight(w, 'Daily chat budget', sub);

    const segW = Math.min(100, (w - ROW_PAD * 2 - SPACING.xs * 4) / 5);
    const btnH = Math.max(TOUCH_TARGET, FONT.size.sm + SPACING.lg);
    // The longest readout (200k stop) gives the tallest wrap to reserve room for.
    const readoutH = this.measureWrappedHeight(
      `About 200k tokens/day — roughly $0.60 at most. Spend nothing by turning lively chat off.`,
      FONT.size.sm,
      w - ROW_PAD * 2,
    );

    const height = ROW_PAD + titleBlockH + SPACING.md + btnH + SPACING.sm + readoutH + ROW_PAD;
    const { card } = this.makeCard(w, y, height);
    this.registerRow(y, height, 'budget');
    const top = -height / 2 + ROW_PAD;
    const afterTitle = this.addTitle(card, w, top, 'Daily chat budget', sub);

    const btnY = afterTitle + SPACING.md + btnH / 2;
    const totalW = segW * 5 + SPACING.xs * 4;
    const startX = -totalW / 2 + segW / 2;
    this.budgetButtons = [];
    BUDGET_STOPS.forEach((value, i) => {
      const button = new Button(this, startX + i * (segW + SPACING.xs), btnY, {
        label: budgetLabel(value),
        variant: 'ghost',
        width: segW,
        fontSize: FONT.size.sm,
        reducedMotion: this.reduced,
        onClick: () => void this.setBudget(value),
      });
      card.add(button);
      this.budgetButtons.push({ value, button });
    });

    this.budgetReadout = this.add
      .text(0, btnY + btnH / 2 + SPACING.sm, '', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.sm}px`,
        color: CSS.textMuted,
        align: 'center',
        wordWrap: { width: w - ROW_PAD * 2 },
      })
      .setOrigin(0.5, 0);
    card.add(this.budgetReadout);

    this.refreshBudget();
    return y + height + SPACING.md;
  }

  private async setBudget(value: number): Promise<void> {
    this.settings = { ...this.settings, dailyTokenBudget: value };
    this.refreshBudget();
    await this.session.updateSettings({ dailyTokenBudget: value });
  }

  private refreshBudget(): void {
    const current = this.settings.dailyTokenBudget;
    // Highlight the nearest stop (custom imported values still read sensibly).
    const nearest = BUDGET_STOPS.reduce((best, v) =>
      Math.abs(v - current) < Math.abs(best - current) ? v : best,
    );
    for (const { value, button } of this.budgetButtons) {
      button.setAlpha(value === nearest ? 1 : 0.55);
    }
    const approxCost = (current / 1_000_000) * APPROX_USD_PER_MTOK;
    this.budgetReadout?.setText(
      `About ${formatTokens(current)} tokens/day — roughly $${approxCost.toFixed(2)} at most. ` +
        'Spend nothing by turning lively chat off.',
    );
  }

  // ── reduced motion ──────────────────────────────────────────────────────────

  private addMotionRow(w: number, y: number): number {
    const sub = 'Calmer still — fades and gentle animations become instant.';
    const toggleW = Math.min(110, Math.max(TOUCH_TARGET * 1.8, w * 0.28));
    const titleW = w - ROW_PAD * 2 - toggleW - SPACING.md;
    const titleBlockH = this.measureTitleHeight(w, 'Reduced motion', sub, titleW);
    const btnH = Math.max(TOUCH_TARGET, FONT.size.md + SPACING.lg);

    const height = ROW_PAD + Math.max(titleBlockH, btnH) + ROW_PAD;
    const { card } = this.makeCard(w, y, height);
    this.registerRow(y, height, 'motion');
    const top = -height / 2 + ROW_PAD;
    this.addTitle(card, w, top, 'Reduced motion', sub, titleW);

    this.motionButton = new Button(this, w / 2 - ROW_PAD - toggleW / 2, top + btnH / 2, {
      label: '',
      variant: 'primary',
      width: toggleW,
      reducedMotion: this.reduced,
      onClick: () => void this.toggleMotion(),
    });
    card.add(this.motionButton);
    this.refreshMotion();
    return y + height + SPACING.md;
  }

  private async toggleMotion(): Promise<void> {
    const next = !this.settings.reducedMotion;
    this.settings = { ...this.settings, reducedMotion: next };
    this.reduced = next;
    // Keep the shared transition flag in sync immediately, app-wide.
    setReducedMotion(next);
    this.refreshMotion();
    await this.session.updateSettings({ reducedMotion: next });
  }

  private refreshMotion(): void {
    const on = this.settings.reducedMotion;
    this.motionButton?.setLabel(on ? 'On' : 'Off');
    this.motionButton?.setAlpha(on ? 1 : 0.7);
  }

  // ── save: export / import ─────────────────────────────────────────────────────

  private addSaveRow(w: number, y: number): number {
    const sub =
      'The save is your world — back it up to a file any time, or bring one in. Nothing leaves your device.';
    const titleBlockH = this.measureTitleHeight(w, 'Your save', sub);
    const btnH = Math.max(TOUCH_TARGET, FONT.size.md + SPACING.lg);

    const height = ROW_PAD + titleBlockH + SPACING.md + btnH + ROW_PAD;
    const { card } = this.makeCard(w, y, height);
    this.registerRow(y, height, 'save');
    const top = -height / 2 + ROW_PAD;
    const afterTitle = this.addTitle(card, w, top, 'Your save', sub);

    const btnY = afterTitle + SPACING.md + btnH / 2;
    const bw = Math.min(180, (w - ROW_PAD * 2 - SPACING.md) / 2);
    const exportBtn = new Button(this, -bw / 2 - SPACING.sm, btnY, {
      label: 'Export to file',
      variant: 'ghost',
      width: bw,
      reducedMotion: this.reduced,
      onClick: () => this.exportSave(),
    });
    const importBtn = new Button(this, bw / 2 + SPACING.sm, btnY, {
      label: 'Import from file',
      variant: 'ghost',
      width: bw,
      reducedMotion: this.reduced,
      onClick: () => this.importSave(),
    });
    card.add([exportBtn, importBtn]);
    return y + height + SPACING.md;
  }

  /** Serialize the live world and trigger a browser download. */
  private exportSave(): void {
    try {
      const json = this.session.exportToString();
      if (typeof document === 'undefined' || typeof URL === 'undefined') {
        this.toast('Export is only available in the browser.', COLORS.rose);
        return;
      }
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `parallel-routes-${this.session.currentDate}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      this.toast('Saved a backup of your world.', COLORS.accent);
    } catch {
      this.toast('Could not export just now — your world is still safe.', COLORS.rose);
    }
  }

  /** Open a file picker, import the chosen save, and persist + soft-reset to Town. */
  private importSave(): void {
    if (typeof document === 'undefined') {
      this.toast('Import is only available in the browser.', COLORS.rose);
      return;
    }
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json,.json';
    input.style.display = 'none';
    input.addEventListener('change', () => {
      const file = input.files?.[0];
      input.remove();
      if (!file) return;
      void this.applyImport(file);
    });
    document.body.appendChild(input);
    input.click();
  }

  private async applyImport(file: File): Promise<void> {
    try {
      const text = await file.text();
      this.session.importFromString(text);
      await this.session.save();
      // Refresh the local mirror + UI to the imported world, then step home so the
      // whole game re-reads the new save cleanly.
      this.settings = { ...this.session.currentSettings };
      setReducedMotion(this.settings.reducedMotion);
      this.toast('Welcome back — your world is loaded.', COLORS.accent);
      this.time.delayedCall(900, () => {
        if (this.scene.isActive()) goTo(this, 'Town');
      });
    } catch {
      this.toast("That file isn't a Parallel Routes save.", COLORS.rose);
    }
  }

  // ── credits ────────────────────────────────────────────────────────────────────

  private addCreditsRow(w: number, y: number): number {
    const subText =
      'The overworld sprites and portraits are made by a generous community. They deserve the thanks.';

    // Measure the wrapped title block BEFORE sizing the card, so the button always
    // lands clearly BELOW the paragraph — even when a narrow phone wraps the subtitle
    // to several lines (the old fixed 116px card let a tall subtitle sit on the button).
    const titleBlockH = this.measureTitleHeight(w, 'Credits', subText);

    const btnW = Math.min(240, w - ROW_PAD * 2);
    const probe = new Button(this, 0, 0, {
      label: 'View the spriters',
      variant: 'warm',
      width: btnW,
      reducedMotion: this.reduced,
      onClick: () => {},
    });
    const btnH = probe.buttonHeight;
    probe.destroy();

    const height = ROW_PAD + titleBlockH + SPACING.md + btnH + ROW_PAD;
    const { card } = this.makeCard(w, y, height);
    this.registerRow(y, height, 'credits');
    const top = -height / 2 + ROW_PAD;
    const afterTitle = this.addTitle(card, w, top, 'Credits', subText);

    const btn = new Button(this, 0, 0, {
      label: 'View the spriters',
      variant: 'warm',
      width: btnW,
      reducedMotion: this.reduced,
      onClick: () => this.openCredits(),
    });
    btn.setY(afterTitle + SPACING.sm + btn.buttonHeight / 2);
    card.add(btn);
    return y + height + SPACING.md;
  }

  /**
   * Measure the pixel height of a card's title + wrapped subtitle block (mirrors
   * addTitle's layout) without adding anything to the scene, so a row can size its
   * card to fit the wrapped text before placing controls under it.
   */
  private measureTitleHeight(w: number, title: string, sub: string, subWrapWidth?: number): number {
    const wrapW = subWrapWidth ?? w - ROW_PAD * 2;
    const t = this.add.text(0, 0, title, {
      fontFamily: FONT.family,
      fontSize: `${FONT.size.lg}px`,
      fontStyle: 'bold',
      wordWrap: { width: wrapW },
    });
    const s = this.add.text(0, 0, sub, {
      fontFamily: FONT.family,
      fontSize: `${FONT.size.sm}px`,
      wordWrap: { width: wrapW },
    });
    const h = t.height + SPACING.xs + s.height + SPACING.xs;
    t.destroy();
    s.destroy();
    return h;
  }

  /**
   * Measure the pixel height a string wraps to at `fontSize` within `wrapWidth`,
   * without adding anything lasting to the scene. The core primitive of the
   * measure-to-content fix: a row reserves exactly the room its widest wrap needs,
   * so help text never overlaps the controls below it on a narrow phone.
   */
  private measureWrappedHeight(text: string, fontSize: number, wrapWidth: number): number {
    const t = this.add.text(0, 0, text, {
      fontFamily: FONT.family,
      fontSize: `${fontSize}px`,
      wordWrap: { width: wrapWidth },
    });
    const h = t.height;
    t.destroy();
    return h;
  }

  /** A soft full-panel overlay listing every crediting spriter (CC-NC attribution). */
  private openCredits(): void {
    if (this.creditsOverlay) return;
    const safe = this.layout.safe;
    const overlay = this.add.container(0, 0).setDepth(1000);
    this.creditsOverlay = overlay;

    const scrim = this.add.graphics();
    scrim.fillStyle(COLORS.scrim, 0.6);
    scrim.fillRect(safe.x, safe.y, safe.width, safe.height);
    scrim.setInteractive(
      new Phaser.Geom.Rectangle(safe.x, safe.y, safe.width, safe.height),
      Phaser.Geom.Rectangle.Contains,
    );
    overlay.add(scrim);

    const pw = Math.min(CONTENT_MAX_WIDTH, safe.width - SPACING.lg);
    const ph = Math.min(safe.height - SPACING.lg, 560);
    const cx = this.layout.centerX;
    const cy = this.layout.centerY;
    const panel = new Panel(this, cx, cy, { width: pw, height: ph, shadow: true });
    overlay.add(panel);

    const title = this.add
      .text(cx, cy - ph / 2 + ROW_PAD, 'With thanks to the spriters', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.lg}px`,
        color: CSS.warm,
        fontStyle: 'bold',
      })
      .setOrigin(0.5, 0);
    overlay.add(title);

    const intro = this.add
      .text(
        cx,
        title.y + title.height + SPACING.xs,
        'Overworld art © its creators, used under a non-commercial Creative Commons license.',
        {
          fontFamily: FONT.family,
          fontSize: `${FONT.size.xs}px`,
          color: CSS.textMuted,
          align: 'center',
          wordWrap: { width: pw - ROW_PAD * 2 },
        },
      )
      .setOrigin(0.5, 0);
    overlay.add(intro);

    const listTop = intro.y + intro.height + SPACING.md;
    const listBottom = cy + ph / 2 - ROW_PAD - TOUCH_TARGET - SPACING.md;
    const listH = Math.max(TOUCH_TARGET, listBottom - listTop);
    const innerW = pw - ROW_PAD * 2;
    const list = new ScrollList(this, cx, listTop + listH / 2, { width: innerW, height: listH });
    overlay.add(list);

    const credits = this.session.getCredits();
    const index = this.session.bundle.index;
    if (credits.length === 0) {
      const empty = this.add
        .text(0, 0, 'Credits will appear here once the sprite set is ingested.', {
          fontFamily: FONT.family,
          fontSize: `${FONT.size.sm}px`,
          color: CSS.textFaint,
          align: 'center',
          wordWrap: { width: innerW - SPACING.md },
        })
        .setOrigin(0.5, 0);
      list.addItem(empty as Phaser.GameObjects.Text & { y: number }, empty.height + SPACING.md);
      list.setContentHeight(empty.height + SPACING.md);
    } else {
      let cursor = 0;
      for (const c of credits) {
        const species = index.speciesById(c.speciesId);
        const label = species?.name ?? c.speciesId;
        const names = c.contributorNames.join(', ') || '—';
        const line = this.add.text(-innerW / 2 + SPACING.sm, 0, `${label}\n${names}`, {
          fontFamily: FONT.family,
          fontSize: `${FONT.size.sm}px`,
          color: CSS.text,
          wordWrap: { width: innerW - SPACING.md },
          lineSpacing: 2,
        });
        line.setColor(CSS.text);
        // Two-tone: name line muted via a second text would be heavier; keep it light.
        const rowH = line.height + SPACING.sm;
        line.setY(cursor + SPACING.xs);
        list.addItem(line as Phaser.GameObjects.Text & { y: number }, rowH);
        cursor += rowH;
      }
      list.setContentHeight(cursor);
    }

    const close = new Button(this, cx, cy + ph / 2 - ROW_PAD - TOUCH_TARGET / 2 + 4, {
      label: 'Close',
      variant: 'primary',
      width: Math.min(200, innerW),
      reducedMotion: this.reduced,
      onClick: () => this.closeCredits(),
    });
    overlay.add(close);

    // Tapping the scrim outside the panel also closes it.
    scrim.on(Phaser.Input.Events.POINTER_DOWN, () => this.closeCredits());
  }

  // ── helpers ────────────────────────────────────────────────────────────────────

  private toast(text: string, accent: number): void {
    const safe = this.layout.safe;
    new Toast(this, this.layout.centerX, safe.y + safe.height - SPACING.xxl, {
      text,
      accent,
      reducedMotion: this.reduced,
      width: Math.min(360, safe.width - SPACING.lg),
    });
  }

  private goBack(): void {
    goTo(this, 'Town');
  }

  private contentWidth(): number {
    return Math.min(CONTENT_MAX_WIDTH, this.layout.safe.width - SPACING.lg);
  }

  // ── layout ────────────────────────────────────────────────────────────────────

  private relayout(): void {
    const safe = this.layout.safe;
    const cx = this.layout.centerX;
    const headerY = safe.y + SPACING.xl;

    const w = this.contentWidth();
    this.heading?.setPosition(cx - w / 2, headerY);
    this.backButton?.setPosition(cx + w / 2 - (this.backButton?.buttonWidth ?? 120) / 2, headerY);

    const listTop = headerY + SPACING.xl + SPACING.sm;
    const listBottom = safe.y + safe.height - SPACING.lg;
    const listH = Math.max(TOUCH_TARGET, listBottom - listTop);
    this.listViewH = listH;
    if (this.list) {
      this.list.setPosition(cx, listTop + listH / 2);
      this.list.resize(w, listH);
    }

    // Re-measure the cards if the content width actually changed (orientation flip,
    // window resize) so wrapped help text never overlaps the controls below it.
    if (w !== this.builtWidth) this.rebuildRows();

    // Re-seat the keyboard focus ring on the (possibly moved) focused card.
    if (this.rowIndex >= 0) this.setRow(this.rowIndex);
    else this.focusRing?.hide();
  }
}

/** A short token-budget label for a segment button (e.g. "50k"). */
function budgetLabel(value: number): string {
  if (value >= 1_000_000) return `${value / 1_000_000}M`;
  return `${Math.round(value / 1000)}k`;
}

/** A friendly token count for the cost readout. */
function formatTokens(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1000) return `${Math.round(value / 1000)}k`;
  return `${value}`;
}
